import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { interviewerApi, mentorApi, userApi } from '../../services/api';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import ProfileAvatar from '../../components/UI/ProfileAvatar';
import { buildUserInfoJson, parseUserInfoJson } from '../../utils/userInfoJson';
import { getUserProfileId } from '../../utils/userProfile';
import { isCourseOwner, isInterviewer, isMentor } from '../../utils/roles';

const selectClassName =
  'mt-1 w-full rounded-md border-gray-300';

type ApplicationStatus = {
  role: string;
  displayStatus: string;
  rejectionReason?: string | null;
};

const ProfilePage: React.FC = () => {
  const { user, fetchUserProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canGoToDashboard = Boolean(getUserProfileId(user));
  const photoRequired = Boolean(user?.needsProfilePhoto) || Boolean((location.state as { requirePhoto?: boolean } | null)?.requirePhoto);
  const mustHavePhoto = isCourseOwner(user) || isMentor(user) || isInterviewer(user);
  const [username, setUsername] = useState(user?.username || '');
  const [professionalTitle, setProfessionalTitle] = useState(user?.professionalTitle || '');
  const [biography, setBiography] = useState(user?.biography || '');
  const [linkedInUrl, setLinkedInUrl] = useState(user?.linkedInUrl || '');
  const [profileImageUrl, setProfileImageUrl] = useState(user?.displayImageUrl || user?.profileImageUrl || '');
  const [college, setCollege] = useState('');
  const [degree, setDegree] = useState('');
  const [educationStatus, setEducationStatus] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [role, setRole] = useState('');
  const [locationValue, setLocationValue] = useState('');
  const [address, setAddress] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [applications, setApplications] = useState<ApplicationStatus[]>([]);

  const memberSinceRaw = user?.dateCreated
    || (user as { DateCreated?: string } | null)?.DateCreated
    || null;
  const memberSince = memberSinceRaw
    ? new Date(memberSinceRaw).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  useEffect(() => {
    setUsername(user?.username || '');
    setProfessionalTitle(user?.professionalTitle || '');
    setBiography(user?.biography || '');
    setLinkedInUrl(user?.linkedInUrl || '');
    setProfileImageUrl(user?.displayImageUrl || user?.profileImageUrl || '');
    const extended = parseUserInfoJson(user?.userInfoJson);
    setCollege(extended.college ?? '');
    setDegree(extended.degree ?? '');
    setEducationStatus(extended.educationStatus ?? '');
    setIsWorking(Boolean(extended.isWorking));
    setCompanyName(extended.companyName ?? '');
    setRole(extended.role ?? '');
    setLocationValue(extended.location ?? '');
    setAddress(extended.address ?? '');
  }, [user]);

  useEffect(() => {
    if (!user?.email) return;
    Promise.all([
      mentorApi.myApplication().then((r) => r.data).catch(() => null),
      interviewerApi.myApplication().then((r) => r.data).catch(() => null),
    ]).then(([mentor, interviewer]) => {
      const rows: ApplicationStatus[] = [];
      if (mentor?.hasApplication && mentor.displayStatus) {
        rows.push({
          role: 'Mentor',
          displayStatus: mentor.displayStatus,
          rejectionReason: mentor.rejectionReason,
        });
      }
      if (interviewer?.hasApplication && interviewer.displayStatus) {
        rows.push({
          role: 'Interviewer',
          displayStatus: interviewer.displayStatus,
          rejectionReason: interviewer.rejectionReason,
        });
      }
      setApplications(rows);
    });
  }, [user?.email]);

  const uploadPhoto = async (file: File | null) => {
    if (!file) return;
    if (file.size > 100 * 1024) {
      setMessage('Profile photos must be 100 KB or smaller.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setMessage('Choose a JPEG, PNG, WebP, or GIF image.');
      return;
    }
    setUploading(true);
    setMessage(null);
    try {
      const response = await userApi.uploadProfilePhoto(file);
      if (!response.data.success) {
        setMessage(response.data.message || 'Could not upload the photo.');
        return;
      }
      setProfileImageUrl(response.data.displayImageUrl || response.data.profileImageUrl || '');
      await fetchUserProfile();
      setMessage('Profile photo updated.');
    } catch (err: unknown) {
      setMessage(err instanceof Error ? err.message : 'Could not upload the photo.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <div className="surface-card p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Profile</h1>
          <p className="mt-2 text-gray-600">This is the same identity used across IDCoder.</p>
          {memberSince && <p className="mt-1 text-sm text-gray-500">Member since {memberSince}</p>}
        </div>
        {canGoToDashboard && !photoRequired ? (
          <Link to="/dashboard">
            <Button type="button" variant="outline">Dashboard</Button>
          </Link>
        ) : null}
      </div>

      {photoRequired && (
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Upload a profile photo to continue. Course owners, mentors, and interviewers need a photo on their profile.
        </p>
      )}

      {applications.length > 0 && (
        <div className="mt-4 space-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <p className="text-sm font-medium text-gray-900">Applications</p>
          {applications.map((app) => (
            <div key={app.role} className="text-sm text-gray-700">
              <span className="font-medium">{app.role}:</span>{' '}
              <span>{app.displayStatus}</span>
              {app.displayStatus === 'Rejected' && app.rejectionReason ? (
                <span className="block text-gray-500 mt-0.5">{app.rejectionReason}</span>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <form className="mt-6 space-y-6" onSubmit={async (e) => {
        e.preventDefault();
        if (mustHavePhoto && !user?.profileImageUrl) {
          setMessage('Upload a profile photo before saving.');
          return;
        }
        setSaving(true);
        setMessage(null);
        const userInfoJson = buildUserInfoJson({
          college,
          degree,
          educationStatus: educationStatus as '' | 'completed' | 'pursuing',
          isWorking,
          companyName,
          role,
          location: locationValue,
          address,
        });
        try {
          await userApi.updateProfile({
            username,
            professionalTitle,
            biography,
            linkedInUrl,
            profileImageUrl: user?.profileImageUrl || profileImageUrl,
            userInfoJson,
          });
          await fetchUserProfile();
          navigate('/dashboard');
        } catch {
          setMessage('Could not save profile. Please try again.');
        } finally {
          setSaving(false);
        }
      }}>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <ProfileAvatar src={profileImageUrl} className="h-20 w-20 rounded-full object-cover border border-gray-200" />
            <div>
              <p className="text-sm font-medium text-gray-900">Profile photo {mustHavePhoto ? '(required)' : '(optional)'}</p>
              <p className="text-xs text-gray-500">JPEG, PNG, WebP, or GIF. Maximum 100 KB.</p>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="mt-2 block text-sm"
                disabled={uploading}
                onChange={(e) => void uploadPhoto(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <Input id="username" label="Name" value={username} onChange={(e) => setUsername(e.target.value)} />
          <Input id="title" label="Professional title" value={professionalTitle} onChange={(e) => setProfessionalTitle(e.target.value)} />
          <label className="block text-sm font-medium text-gray-700">Biography
            <textarea className="mt-1 w-full rounded-md border-gray-300" rows={4} value={biography} onChange={(e) => setBiography(e.target.value)} />
          </label>
          <Input id="li" label="LinkedIn URL (optional)" value={linkedInUrl} onChange={(e) => setLinkedInUrl(e.target.value)} placeholder="https://www.linkedin.com/in/your-profile" />
        </div>

        <div>
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Education (optional)</h2>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            <Input id="college" label="College" value={college} onChange={(e) => setCollege(e.target.value)} />
            <Input id="degree" label="Degree" value={degree} onChange={(e) => setDegree(e.target.value)} />
            <label className="block text-sm font-medium text-gray-700">
              Completed or pursuing
              <select id="educationStatus" className={selectClassName} value={educationStatus} onChange={(e) => setEducationStatus(e.target.value)}>
                <option value="">Select status</option>
                <option value="completed">Completed</option>
                <option value="pursuing">Pursuing</option>
              </select>
            </label>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Employment (optional)</h2>
          <div className="mt-3 space-y-4">
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={isWorking}
                onChange={(e) => setIsWorking(e.target.checked)}
              />
              Currently working
            </label>
            {isWorking && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Input id="companyName" label="Company name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                <Input id="role" label="Role" value={role} onChange={(e) => setRole(e.target.value)} />
              </div>
            )}
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Location (optional)</h2>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            <Input id="location" label="Location" value={locationValue} onChange={(e) => setLocationValue(e.target.value)} />
            <Input id="address" label="Address for communication" value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
        </div>

        {message && <p className="text-error-700">{message}</p>}
        <Button type="submit" isLoading={saving || uploading}>Save</Button>
      </form>
      </div>
    </div>
  );
};

export default ProfilePage;
